import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useDispatch, useSelector } from 'react-redux';
import {
	NoLeads,
	EnvelopeCommunications,
	EnvelopeCommunicationsFilled,
	ListsGrey,
	Note,
	Chevron,
	Website,
	Yelp,
	Instagram,
	FlagIcon,
	TrashNew,
	Portrait,
	CloseThin,
	Google,
	Refresh,
	GoldCalendar
} from '@/assets//images';
import Twitter from '@/assets/images/twitter-logo.png';
import Youtube from '@/assets/images/youtube-transparant.png';
import Pinterest from '@/assets/images/pinterest-transparant.png';
import Tiktok from '@/assets/images/tiktok-transparant.png';
import Snapchat from '@/assets/images/snapchat-transparant.png';
import Lottie from 'lottie-react';
import ClockPng from '@/assets/images/clock.png';
import LeadsPng from '@/assets/images/leads.png';
import IconWarningOutlinePng from '@/assets/images/icon-warning-outline.png';
import EnvelopeOpenedPng from '@/assets/images/envelope-opened.png';
import PhonePng from '@/assets/images/phone.png';
import FacebookPng from '@/assets/images/facebook.png';
import MessengerUnfilledPng from '@/assets/images/messenger-unfilled.png';
import type { RootState } from '@/rootReducer';
import type { Activity } from '@/modules/dashboard/dashboardTypes';
import {
	dashboardGetActivityEmailOutbound,
	dashboardGetActivityEmailInbound
} from '@/modules/dashboard/dashboardActions';
import loadingAnimation from './activity-email-loading-animation.json';
import './Activity.scss';

interface Props {
	listItemOpened: boolean;
	activity: Activity;
}

const Activity = ({ listItemOpened, activity }: Props): JSX.Element => {
	const [activityEmails, activityEmailsLoading, activityEmailsFailure, user] =
		useSelector((state: RootState) => [
			state.dashboard.getIn(['activities', 'emails', 'data']).toJS(),
			state.dashboard.getIn(['activities', 'emails', 'isLoading']).toJS(),
			state.dashboard.getIn(['activities', 'emails', 'failure']).toJS(),
			state.user.getIn(['authorize', 'user']).toJS()
		]);
	const [activityOpened, setActivityOpened] = useState<boolean>(false);
	const dispatch = useDispatch();
	const getActivityEmailOutboundDispatch = ({ emailId }) =>
		dispatch(dashboardGetActivityEmailOutbound({ emailId }));

	const getActivityEmailInboundDispatch = ({ emailId }) =>
		dispatch(dashboardGetActivityEmailInbound({ emailId }));

	useEffect(() => {
		if (!listItemOpened) {
			setActivityOpened(false);
		}
	}, [listItemOpened]);

	return (
		<div
			key={activity.id}
			className={`activity ${listItemOpened || listItemOpened === undefined ? 'opened' : ''}`}
			onClick={e => {
				e.stopPropagation();
			}}
		>
			<span className="activity-bullet" />
			{activity?.type === 'leadStatusChange' &&
				activity?.data?.status && (
					<img
						src={LeadsPng}
						alt="Marked as lead"
						className="activity-icon leads"
					/>
				)}
			{activity?.type === 'leadStatusChange' &&
				activity?.data?.status === false && (
					<NoLeads className="activity-icon" />
				)}
			{((activity?.type === 'leadStatusChange' &&
				activity?.data?.status === undefined) ||
				activity?.type === 'cancelledScheduledEmail' ||
				activity?.type === 'businessRemovedFromList') && (
				<CloseThin className="activity-icon leads-removed" />
			)}
			{activity?.type === 'emailReplied' && (
				<EnvelopeCommunicationsFilled className="activity-icon replied" />
			)}
			{activity?.type === 'emailSent' && (
				<EnvelopeCommunications className="activity-icon sent" />
			)}
			{activity?.type === 'emailQueued' && (
				<img
					src={ClockPng}
					alt="Clock"
					className="activity-icon queued"
				/>
			)}
			{activity?.type === 'emailFailed' && (
				<img
					src={IconWarningOutlinePng}
					alt="Email failed"
					className="activity-icon failed"
				/>
			)}
			{activity?.type === 'note' && (
				<Note className="activity-icon note" />
			)}
			{activity?.type === 'phoneContacted' && (
				<img
					src={PhonePng}
					alt="Phone"
					className="activity-icon phone"
				/>
			)}
			{activity?.type === 'messengerContacted' && (
				<img
					src={MessengerUnfilledPng}
					alt="FB messenger"
					className="activity-icon messenger"
				/>
			)}
			{activity?.type === 'instagramMessengerContacted' && (
				<Instagram className="activity-icon instagram" />
			)}
			{activity?.type === 'businessAddedToList' && (
				<ListsGrey className="activity-icon list" />
			)}
			{activity?.type === 'emailOpened' && (
				<img
					src={EnvelopeOpenedPng}
					alt="Email opened"
					className="activity-icon email-opened"
				/>
			)}
			{activity?.type === 'websiteOpened' && (
				<Website className="activity-icon website" />
			)}
			{activity?.type === 'yelpOpened' && (
				<Yelp className="activity-icon yelp" />
			)}
			{activity?.type === 'instagramOpened' && (
				<Instagram className="activity-icon instagram" />
			)}
			{activity?.type === 'pinterestOpened' && (
				<img src={Pinterest} className="activity-icon pinterest" />
			)}
			{activity?.type === 'twitterOpened' && (
				<img src={Twitter} className="activity-icon twitter" />
			)}
			{activity?.type === 'youtubeOpened' && (
				<img src={Youtube} className="activity-icon youtube" />
			)}
			{activity?.type === 'tiktokOpened' && (
				<img src={Tiktok} className="activity-icon tiktok" />
			)}
			{activity?.type === 'snapchatOpened' && (
				<img src={Snapchat} className="activity-icon snapchat" />
			)}
			{activity?.type === 'reminderCreated' && (
				<FlagIcon className="activity-icon reminder-created" />
			)}
			{(activity?.type === 'reminderDeleted' ||
				activity?.type === 'customContactInfoDeleted') && (
				<TrashNew className="activity-icon reminder-deleted" />
			)}
			{activity?.type === 'customContactInfoCreated' && (
				<Portrait className="activity-icon custom-contact-info-created" />
			)}
			{activity?.type === 'facebookOpened' && (
				<img
					src={FacebookPng}
					alt="Facebook"
					className="activity-icon facebook"
				/>
			)}
			{activity?.type === 'googleOpened' && (
				<Google className="activity-icon google" />
			)}
			{activity?.type === 'emailScheduled' && (
				<GoldCalendar className="activity-icon calendar" />
			)}
			<span
				className={`activity-name ${
					activity?.type === 'note' ||
					activity?.type === 'emailReplied' ||
					activity?.type === 'emailSent' ||
					activity?.type === 'emailQueued' ||
					activity?.type === 'emailScheduled' ||
					activity?.type === 'cancelledScheduledEmail' ||
					activity?.type === 'emailFailed'
						? 'expandable'
						: ''
				}`}
				onClick={() => {
					if (
						activity?.type === 'note' ||
						activity?.type === 'emailReplied' ||
						activity?.type === 'emailSent' ||
						activity?.type === 'emailQueued' ||
						activity?.type === 'emailScheduled' ||
						activity?.type === 'cancelledScheduledEmail' ||
						activity?.type === 'emailFailed'
					) {
						setActivityOpened(!activityOpened);
					}

					if (
						(activity?.type === 'emailSent' ||
							activity?.type === 'emailQueued' ||
							activity?.type === 'emailFailed') &&
						!activityOpened &&
						!activityEmails[`${activity?.data?.emailId}`]
					) {
						getActivityEmailOutboundDispatch({
							emailId: activity?.data?.emailId
						});
					}

					if (
						activity?.type === 'emailReplied' &&
						!activityOpened &&
						!activityEmails[`${activity?.data?.inboundEmailId}`]
					) {
						getActivityEmailInboundDispatch({
							emailId: activity?.data?.inboundEmailId
						});
					}
				}}
			>
				<span className="activity-name-text">{activity.header}</span>
				{(activity?.type === 'emailReplied' ||
					activity?.type === 'emailSent' ||
					activity?.type === 'emailQueued' ||
					activity?.type === 'emailFailed' ||
					activity?.type === 'emailScheduled' ||
					activity?.type === 'cancelledScheduledEmail' ||
					activity?.type === 'note') && (
					<Chevron
						className={`activity-chevron-icon ${activityOpened ? 'activity-opened' : ''}`}
					/>
				)}
			</span>
			<span className="activity-date">
				{new Date(activity.createdAt).toLocaleString('en-US', {
					timeStyle: 'short',
					dateStyle: 'medium',
					timeZone: user?.timeZone || 'UTC'
				})}
			</span>
			<span
				className={`activity-dropdown-text ${
					(activity?.type === 'note' ||
						activity?.type === 'emailReplied' ||
						activity?.type === 'emailSent' ||
						activity?.type === 'emailQueued' ||
						activity?.type === 'emailFailed' ||
						activity?.type === 'emailScheduled' ||
						activity?.type === 'cancelledScheduledEmail') &&
					activityOpened
						? 'activity-opened'
						: ''
				}`}
			>
				{activity?.type === 'note' && activity?.data?.text}
				{(activity?.type === 'emailScheduled' ||
					activity?.type === 'cancelledScheduledEmail') && (
					<div
						dangerouslySetInnerHTML={{
							__html: DOMPurify.sanitize(activity?.data?.body)
						}}
					/>
				)}
				{(activityEmailsLoading[`${activity?.data?.emailId}`] ||
					activityEmailsLoading[
						`${activity?.data?.inboundEmailId}`
					]) && (
					<div className="activity-dropdown-text-loader-div">
						<Lottie
							animationData={loadingAnimation}
							loop
							style={{
								width: 250,
								height: 100,
								position: 'absolute',
								top: '-25px'
							}}
						/>
						<span>Loading email</span>
					</div>
				)}
				{!activityEmailsLoading[`${activity?.data?.emailId}`] &&
					(activity.type === 'emailSent' ||
						activity.type === 'emailQueued' ||
						activity.type === 'emailFailed') &&
					activityEmails[`${activity?.data?.emailId}`] && (
						<div
							dangerouslySetInnerHTML={{
								__html: DOMPurify.sanitize(
									activityEmails[`${activity?.data?.emailId}`]
										?.body
								)
							}}
						/>
					)}
				{!activityEmailsLoading[`${activity?.data?.inboundEmailId}`] &&
					activity.type === 'emailReplied' &&
					activityEmails[`${activity?.data?.inboundEmailId}`] && (
						<div
							dangerouslySetInnerHTML={{
								__html: DOMPurify.sanitize(
									activityEmails[
										`${activity?.data?.inboundEmailId}`
									]?.body
								)
							}}
						/>
					)}
				{((activityEmailsFailure[`${activity?.data?.emailId}`] &&
					!activityEmailsLoading[`${activity?.data?.emailId}`]) ||
					(activityEmailsFailure[
						`${activity?.data?.inboundEmailId}`
					] &&
						!activityEmailsLoading[
							`${activity?.data?.inboundEmailId}`
						])) && (
					<div className="activity-dropdown-text-failure-div">
						<Refresh
							className="activity-dropdown-text-failure-icon"
							onClick={() => {
								if (activity?.data?.emailId) {
									getActivityEmailOutboundDispatch({
										emailId: activity?.data?.emailId
									});
								} else if (activity?.data?.inboundEmailId) {
									getActivityEmailInboundDispatch({
										emailId: activity?.data?.inboundEmailId
									});
								}
							}}
						/>
						<span>Email failed to load. Tap to try again.</span>
					</div>
				)}
			</span>
		</div>
	);
};

export default Activity;
